import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import { CompetitionStatus } from './types'

/**
 * A mapping of participant type identifiers to their corresponding labels.
 * @type {Record<string, string>}
 */
export const participantTypeMapping: Record<string, string> = {
    individual: 'Individualni',
    team: 'Timski',
    'team-individual': 'Timski-individualni',
}

/**
 * A mapping of competition status identifiers to their corresponding labels.
 * Can be easily extended to support internationalization (i18n) by providing a hook for translations.
 * @type {Record<CompetitionStatus, string>}
 */
const getCompetitionStatusMapping = (): Record<CompetitionStatus, string> => ({
    [CompetitionStatus.NOT_STARTED]: i18next.t(
        'common:form.labels.competition-status.statuses.not-started'
    ),
    [CompetitionStatus.GENERAL_APPLICATIONS]: i18next.t(
        'common:form.labels.competition-status.statuses.open-applications'
    ),
    [CompetitionStatus.INDIVIDUAL_APPLICATIONS]: i18next.t(
        'common:form.labels.competition-status.statuses.team-registration'
    ),
    [CompetitionStatus.CLOSED_APPLICATIONS]: i18next.t(
        'common:form.labels.competition-status.statuses.closed'
    ),
    [CompetitionStatus.ONGOING]: i18next.t(
        'common:form.labels.competition-status.statuses.ongoing'
    ),
    [CompetitionStatus.FINISHED]: i18next.t(
        'common:form.labels.competition-status.statuses.finished'
    ),
    [CompetitionStatus.UNDEFINED]: i18next.t(
        'common:form.labels.competition-status.statuses.undefined'
    ),
})

export const competitionStatusMapping = new Proxy(
    {} as Record<CompetitionStatus, string>,
    {
        get: (_target, key: string) =>
            getCompetitionStatusMapping()[key as CompetitionStatus],
        ownKeys: () => Object.keys(getCompetitionStatusMapping()),
        getOwnPropertyDescriptor: (_target, key: string) => ({
            value: getCompetitionStatusMapping()[key as CompetitionStatus],
            enumerable: true,
            configurable: true,
        }),
    }
)

/**
 * A mapping of competition status identifiers to their corresponding labels in the current language.
 * @type {Record<CompetitionStatus, string>}
 */
export const useCompetitionStatusMapping = () => {
    const { t } = useTranslation()

    return {
        [CompetitionStatus.NOT_STARTED]: t('competition-status.notStarted'),
        [CompetitionStatus.GENERAL_APPLICATIONS]: t(
            'competition-status.generalApplication'
        ),
        [CompetitionStatus.INDIVIDUAL_APPLICATIONS]: t(
            'competition-status.individualApplication'
        ),
        [CompetitionStatus.CLOSED_APPLICATIONS]: t(
            'competition-status.closedApplication'
        ),
        [CompetitionStatus.ONGOING]: t('competition-status.ongoing'),
        [CompetitionStatus.FINISHED]: t('competition-status.finished'),
        [CompetitionStatus.UNDEFINED]: t('competition-status.undefined'),
    }
}

export const queryMappings: Record<string, (value: string) => string> = {
    search: (value: string) => value, // search does not require additional formatting
    sexes: (value: string) => `sexes:${value}`,
    statuses: (value: string) => `statuses:${value}`,
    seasonIds: (value: string) => `seasonIds:${value}`,
    sportIds: (value: string) => `sportIds:${value}`,
}

export const createSerializedKey = (
    endpointName: string,
    queryArgs: Record<string, unknown>,
    mappings: Record<string, (value: string) => string>
) => {
    const keyParts = [
        endpointName,
        ...Object.entries(mappings)
            .map(([key, format]) =>
                queryArgs[key] ? format(queryArgs[key] as string) : null
            )
            .filter(Boolean),
    ]

    return keyParts.join('-')
}

interface CacheItem {
    id: string | number
    [key: string]: unknown
}

interface CacheData {
    items: CacheItem[]
    meta: Record<string, unknown>
}

export const mergeCache = (
    arg: { page?: number; [key: string]: unknown },
    currentCache: CacheData,
    newItems: CacheData
) => {
    if (!currentCache) {
        currentCache = { items: [], meta: {} }
    }

    // If there's pagination and it's a new page, merge unique items
    if (arg.page && arg.page !== currentCache.meta.page) {
        const itemsToMerge: CacheItem[] = Array.isArray(newItems.items)
            ? newItems.items
            : []

        const existingItemIds = new Set(
            currentCache.items.map((item) => item.id)
        )

        // Filter out duplicate items based on their ID
        const uniqueNewItems = itemsToMerge.filter(
            (item) => !existingItemIds.has(item.id)
        )

        // Append unique items to the existing cache
        currentCache.items.push(...uniqueNewItems)

        // Update meta information
        currentCache.meta = newItems.meta
    } else {
        // If no pagination or page is the same, replace the entire cache
        currentCache.items = Array.isArray(newItems.items) ? newItems.items : []
        currentCache.meta = newItems.meta
    }
}
